import {
  AlertTriangle,
  ArrowUpRight,
  CalendarClock,
  CreditCard,
  Gauge,
  History,
  IndianRupee,
  Layers,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { useMemo } from "react";

import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { DASH, formatCompactINR, formatNumber, formatPercent } from "@/lib/cibil/format";
import type { BureauMetrics } from "@/lib/cibil/metrics";
import { dpdRecency, riskFlags } from "@/lib/cibil/metrics";
import type { NormalizedReport } from "@/lib/cibil/normalize";

interface Tile {
  label: string;
  value: string;
  hint?: string | undefined;
  icon: LucideIcon;
  tone?: "default" | "danger";
}

function TileGroup({ title, tiles }: { title: string; tiles: Tile[] }) {
  return (
    <Card className="gap-0 overflow-hidden p-0">
      <div className="border-b border-border px-5 py-3">
        <h3 className="font-display text-sm font-semibold">{title}</h3>
      </div>
      <div className="grid grid-cols-1 divide-y divide-border sm:grid-cols-2 sm:divide-x lg:grid-cols-4 lg:divide-y-0">
        {tiles.map((t) => (
          <div key={t.label} className="flex flex-col gap-2 p-5">
            <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
              <span
                className={cn(
                  "flex size-6 items-center justify-center rounded-md",
                  t.tone === "danger"
                    ? "bg-danger-soft text-destructive"
                    : "bg-accent text-accent-foreground",
                )}
              >
                <t.icon className="size-3.5" />
              </span>
              <span className="leading-tight">{t.label}</span>
            </div>
            <p
              className={cn(
                "font-display text-2xl font-semibold tabular-nums",
                t.tone === "danger" && "text-destructive",
              )}
            >
              {t.value}
            </p>
            {t.hint ? <p className="text-xs text-muted-foreground">{t.hint}</p> : null}
          </div>
        ))}
      </div>
    </Card>
  );
}

export function SummaryCards({
  metrics,
  report,
}: {
  metrics: BureauMetrics;
  report: NormalizedReport;
}) {
  const ref = metrics.referenceDate;
  const flags = useMemo(() => riskFlags(report, ref), [report, ref]);
  const recency = useMemo(() => dpdRecency(report.accounts, ref), [report.accounts, ref]);
  const last12 = recency.find((r) => r.months === 12);
  const last6 = recency.find((r) => r.months === 6);

  const portfolio: Tile[] = [
    {
      label: "Total Active Loan Accounts",
      value: formatNumber(metrics.activeCount),
      hint: `${formatNumber(metrics.closedCount)} closed`,
      icon: Layers,
    },
    {
      label: "Total Sanction / High Credit",
      value: formatCompactINR(metrics.totalSanctioned),
      hint: `Outstanding ${formatCompactINR(metrics.totalOutstanding)}`,
      icon: IndianRupee,
    },
    {
      label: "Credit Utilisation",
      value: formatPercent(metrics.creditUtilisation),
      hint: metrics.utilisationSource,
      icon: Gauge,
    },
    {
      label: "Enquiries in Past 90 Days",
      value: formatNumber(metrics.enquiries90),
      hint: `${formatNumber(metrics.enquiries365)} in last 12 months`,
      icon: CalendarClock,
    },
  ];

  const risk: Tile[] = [
    {
      label: "Delayed Months (Last 12m)",
      value: last12 ? formatNumber(last12.delayedMonths) : DASH,
      hint: `${last6 ? formatNumber(last6.delayedMonths) : DASH} in last 6m · ${formatNumber(metrics.totalDpdRecorded)} till date`,
      icon: History,
      tone: (last12?.delayedMonths ?? 0) > 0 ? "danger" : "default",
    },
    {
      label: "Worst DPD (Last 12m)",
      value: last12?.worstDpd ? `${formatNumber(last12.worstDpd)} days` : DASH,
      hint:
        metrics.highestDpd === null
          ? "No DPD reported till date"
          : `${formatNumber(metrics.highestDpd)} days till date`,
      icon: ArrowUpRight,
      tone: last12?.worstDpd ? "danger" : "default",
    },
    {
      label: "Accounts with DPDs",
      value: formatNumber(metrics.accountsWithDpd),
      hint: last12 ? `${formatNumber(last12.accounts)} delinquent in last 12m` : undefined,
      icon: CreditCard,
      tone: metrics.accountsWithDpd > 0 ? "danger" : "default",
    },
    {
      label: "Current Overdue Amount",
      value: formatCompactINR(metrics.currentOverdue),
      hint: `${formatNumber(
        report.accounts.filter((a) => a.isActive && (a.currentOverdue ?? 0) > 0).length,
      )} active account(s) overdue`,
      icon: AlertTriangle,
      tone: metrics.currentOverdue > 0 ? "danger" : "default",
    },
  ];

  return (
    <div className="grid gap-4">
      <TileGroup title="Portfolio Summary" tiles={portfolio} />
      <TileGroup title="Delinquency Summary" tiles={risk} />
      {flags.length > 0 ? (
        <Card className="gap-0 overflow-hidden rounded-xl border border-border p-0 shadow-none">
          <div className="border-b border-border px-5 py-3.5">
            <h3 className="font-display text-sm font-semibold text-foreground">Risk Flags</h3>
          </div>
          <div className="flex flex-wrap gap-2.5 px-5 py-4">
            {flags.map((f) => (
              <span
                key={f.label}
                title={f.hint}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-medium",
                  f.tone === "danger" && "bg-danger-soft text-destructive",
                  f.tone === "warning" && "bg-warning-soft text-foreground",
                  f.tone === "neutral" && "bg-warning-soft/70 text-foreground",
                )}
              >
                {f.label}
                <span className="tabular-nums font-semibold">{f.value}</span>
              </span>
            ))}
          </div>
        </Card>
      ) : null}
    </div>
  );
}
